import { Injectable } from "@angular/core";
import { MediaType } from "../../../../../../features/media/data/entities/media.entity";
import { DateRange } from "../../../../../../shared/services/date.service";
import { InstagramCarousel } from "../../../domain/models/instagram-carousel";
import { InstagramSingleImage } from "../../../domain/models/instagram-single-image";
import { InstagramStory } from "../../../domain/models/instagram-story";
import {
  InstagramPublication,
  RecommendedSchedule,
} from "../../../domain/models/publication-template";
import "../../../../../../shared/extensions/date.extensions";
import { PublicationEventInput } from "../calendar-event-publication/publication-event-input";
import {
  IPublicationTemplateCalendarEventFactory,
  mapPublicationTemplateToCalendarEvents,
  SocialMediaBorderColor,
  SocialMediaColor,
} from "./calendar-event.mappers";

export const mapInstagramPublicationToCalendarEvent = (
  publication: InstagramPublication,
  schedule: RecommendedSchedule,
): PublicationEventInput => {
  const baseConfig = mapPublicationTemplateToCalendarEvents(
    publication,
    schedule,
  );
  return {
    ...baseConfig,
    backgroundColor: SocialMediaColor.Instagram,
    borderColor: SocialMediaBorderColor.Instagram,
    extendedProps: {
      ...baseConfig.extendedProps,
      icon: "ic_instagram.svg",
    },
  };
};

@Injectable()
export class InstagramCarouselCalendarEventFactory
  implements IPublicationTemplateCalendarEventFactory
{
  public create(
    publication: InstagramCarousel,
    campaignDateRange: DateRange,
  ): PublicationEventInput[] {
    return publication.recommendedSchedules.map((schedule) => {
      const eventConfig = mapInstagramPublicationToCalendarEvent(
        publication,
        schedule,
      );

      return {
        ...eventConfig,
        extendedProps: {
          ...eventConfig.extendedProps,
          image: publication.media[0].thumbnailLink,
          title: "publicationTemplate.thumbnail.instagramCarousel.name",
        },
        editable:
          schedule.startDate.isInDayRange(campaignDateRange) &&
          schedule.startDate.isTodayOrFutureDay(),
      };
    });
  }
}

@Injectable()
export class InstagramStoryCalendarEventFactory
  implements IPublicationTemplateCalendarEventFactory
{
  public create(
    publication: InstagramStory,
    campaignDateRange: DateRange,
  ): PublicationEventInput[] {
    return publication.recommendedSchedules.map((schedule) => {
      const eventConfig = mapInstagramPublicationToCalendarEvent(
        publication,
        schedule,
      );

      return {
        ...eventConfig,
        extendedProps: {
          ...eventConfig.extendedProps,
          image:
            publication.media.mediaType === MediaType.Video
              ? publication.media.thumbnailLink
              : publication.media.link,
          title: "publicationTemplate.thumbnail.instagramStory.name",
        },
        editable:
          schedule.startDate.isInDayRange(campaignDateRange) &&
          schedule.startDate.isTodayOrFutureDay(),
      };
    });
  }
}

@Injectable()
export class InstagramSingleImageCalendarEventFactory
  implements IPublicationTemplateCalendarEventFactory
{
  public create(
    publication: InstagramSingleImage,
    campaignDateRange: DateRange,
  ): PublicationEventInput[] {
    return publication.recommendedSchedules.map((schedule) => {
      const eventConfig = mapInstagramPublicationToCalendarEvent(
        publication,
        schedule,
      );

      return {
        ...eventConfig,
        extendedProps: {
          ...eventConfig.extendedProps,
          image: publication.image.thumbnailLink,
          title: "publicationTemplate.thumbnail.instagramSingleImage.name",
        },
        editable:
          schedule.startDate.isInDayRange(campaignDateRange) &&
          schedule.startDate.isTodayOrFutureDay(),
      };
    });
  }
}
